import { NonNumericOrderDto } from '@planning/app/api'
import { EventTypes } from '@planning/messages/eventsTypes'
import {
  GetNnrOrderByIdQuery,
  GetNnrOrdersByVisitIdQuery,
  GetNnrOrdersQuery,
} from '@planning/messages/queries'
import { GetNnrOrdersByVisitIdsQuery } from '@planning/messages/queries/getNnrOrdersByVisitIdsQueryHandler'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable } from 'mobx'
import { ItemStore } from '../base/ItemStore'
import { CompanyItemStore } from '../externalDriver/CompanyItemStore'
import { OrderItemStore } from '../order/OrderItemStore'
import { RailVisitItemStore } from '../railVisit/RailVisitItemStore'
import { VesselVisitItemStore } from '../vesselVisit/VesselVisitItemStore'
import { INonNumericReleaseItem, NnrOrderItem } from './NnrOrderItem'

export interface INnrOrderSummary {
  visitId: number
  nnrOrders: INonNumericReleaseItem[]
  totalAmount: number
  totalUnassignedContainersForVisit: number
}

export class NnrOrderItemStore extends ItemStore<NonNumericOrderDto, INonNumericReleaseItem> {
  fetchedVisitIds: Set<number> = new Set()
  private orderItemStore?: OrderItemStore
  private hasFetchedAll = false

  constructor(
    private readonly messageBus: IMessageBus,
    private readonly companyItemStore?: CompanyItemStore,
    private readonly vesselVisitItemStore?: VesselVisitItemStore,
    private readonly railVisitItemStore?: RailVisitItemStore,
  ) {
    super(
      (key, data) =>
        new NnrOrderItem(
          key,
          data,
          this.orderItemStore,
          this.companyItemStore,
          this.vesselVisitItemStore,
          this.railVisitItemStore,
        ),
      {
        messageBus,
        fetchFunc: (id: number) => new GetNnrOrderByIdQuery(id),
      },
    )
    makeObservable(this, {
      receiveNnrOrdersMessage: action,
      receiveNnrOrderMessage: action,
      receiveNonNumericOrderUpsertedEventMessage: action,
      receiveNonNumericOrderDeletedEventMessage: action,
      ordersForVesselVisitId: computed,
      orderSummaryForVesselVisitId: computed,
    })

    messageBus.subscribeEvent(GetNnrOrdersQuery.type, this.receiveNnrOrdersMessage)
    messageBus.subscribeEvent(GetNnrOrderByIdQuery.type, this.receiveNnrOrderMessage)
    messageBus.subscribeEvent(GetNnrOrdersByVisitIdQuery.type, this.receiveNnrOrdersMessage)
    messageBus.subscribeEvent(GetNnrOrdersByVisitIdsQuery.type, this.receiveNnrOrdersMessage)
    messageBus.subscribeEvent(
      EventTypes.NonNumericOrderUpsertedEvent,
      this.receiveNonNumericOrderUpsertedEventMessage,
    )

    messageBus.subscribeEvent(
      EventTypes.NonNumericOrderDeletedEvent,
      this.receiveNonNumericOrderDeletedEventMessage,
    )
  }

  connect = (orderItemStore: OrderItemStore) => {
    this.orderItemStore = orderItemStore
  }

  fetchAll = async () => {
    if (this.hasFetchedAll) return

    this.hasFetchedAll = true
    this.hasBeenInitialized = true

    return this.messageBus.dispatchQuery(new GetNnrOrdersQuery(1, 10000))
  }

  fetchByVisitId = async (visitId: number) => {
    if (this.hasFetchedAll || this.fetchedVisitIds.has(visitId)) return

    this.fetchedVisitIds.add(visitId)
    this.hasBeenInitialized = true

    await this.messageBus.dispatchQuery(new GetNnrOrdersByVisitIdQuery(visitId))
  }

  fetchByVisitIds = async (visitIds: number[]) => {
    if (this.hasFetchedAll) return
    const unFetchedIds = visitIds.filter(id => !this.fetchedVisitIds.has(id))

    if (unFetchedIds.length === 0) return

    unFetchedIds.forEach(id => this.fetchedVisitIds.add(id))
    this.hasBeenInitialized = true

    await this.messageBus.dispatchQuery(new GetNnrOrdersByVisitIdsQuery(unFetchedIds))
  }

  get ordersForVesselVisitId() {
    return _(this.elements)
      .values()
      .groupBy(order => order.data.carrierVisitId)
      .value()
  }

  get orderSummaryForVesselVisitId() {
    return _(this.ordersForVesselVisitId)
      .flatMap((values, key) => {
        const nnrOrderItems = values.map(item => item.data)
        const amount = _.sumBy(nnrOrderItems, 'amount')
        return {
          visitId: Number(key),
          nnrOrders: values,
          totalAmount: amount,
          totalUnassignedContainersForVisit: amount - _.sumBy(nnrOrderItems, 'assigned'),
        } as INnrOrderSummary
      })
      .keyBy(aa => aa.visitId)
      .value()
  }

  receiveNnrOrdersMessage = (event: IEvent<NonNumericOrderDto[]>) => {
    if (event.payload) {
      this.upsertBulk(event.payload)
    }
  }
  receiveNnrOrderMessage = (event: IEvent<NonNumericOrderDto>) => {
    if (event.payload) {
      this.upsert(event.payload)
    }
  }

  receiveNonNumericOrderUpsertedEventMessage = (event: IEvent<NonNumericOrderDto>): void => {
    console.log('Upserted Event Received:', event.payload)
    if (event.payload) {
      this.upsert(event.payload)
    }
  }

  receiveNonNumericOrderDeletedEventMessage = (event: IEvent<number>): void => {
    console.log('Deleted Event Received:', event.payload)
    if (event.payload && _.has(this.elements, event.payload)) {
      console.log('Deleting Order:', event.payload)
      this.delete(event.payload)
    }
  }
}
